import { createContext } from 'react';
import { useCallback } from 'react';
import { useContext } from 'react';
import { useState } from 'react';
import { SetCookie } from '../lib/cookies';
import { DeleteCookie } from '../lib/cookies';

import type { ReactNode } from 'react';

type RunAsUser = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

type RunAsContextValue = {
  runAsUser: RunAsUser | null;
  isRunAsActive: boolean;
  startRunAs: (user: RunAsUser) => void;
  exitRunAs: () => void;
  /** Drops impersonation without navigating. The target is a user of one organization, so a switch ends it. */
  clearRunAs: () => void;
};

const runAsStorageKey = 'sgs-run-as';
const runAsCookie = 'sgs-run-as';

const RunAsContext = createContext<RunAsContextValue | undefined>(undefined);

function readPersistedRunAsUser(): RunAsUser | null {
  let user: RunAsUser | null = null;
  if (typeof window !== 'undefined') {
    try {
      const stored = window.sessionStorage.getItem(runAsStorageKey);
      if (stored) {
        user = JSON.parse(stored) as RunAsUser;
      }
    } catch {
      user = null;
    }
  }
  return user;
}

export function RunAsProvider({ children }: { children: ReactNode }) {
  const [runAsUser, setRunAsUser] = useState<RunAsUser | null>(() => readPersistedRunAsUser());

  const startRunAs = useCallback((user: RunAsUser) => {
    window.sessionStorage.setItem(runAsStorageKey, JSON.stringify(user));
    SetCookie(runAsCookie, user.id, { path: '/', sameSite: 'Lax' });
    setRunAsUser(user);
    window.location.href = '/dashboard';
  }, []);

  const clearRunAs = useCallback(() => {
    window.sessionStorage.removeItem(runAsStorageKey);
    DeleteCookie(runAsCookie, { path: '/' });
    setRunAsUser(null);
  }, []);

  const exitRunAs = useCallback(() => {
    clearRunAs();
    window.location.href = '/roles/permissions';
  }, [clearRunAs]);

  const value: RunAsContextValue = {
    runAsUser: runAsUser,
    isRunAsActive: runAsUser !== null,
    startRunAs: startRunAs,
    exitRunAs: exitRunAs,
    clearRunAs: clearRunAs,
  };

  return <RunAsContext.Provider value={value}>{children}</RunAsContext.Provider>;
}

export function useRunAs(): RunAsContextValue {
  const context = useContext(RunAsContext);
  if (context === undefined) {
    throw new Error('useRunAs must be used within a RunAsProvider');
  }
  return context;
}
